import * as React from 'react';
import { Icon } from 'semantic-ui-react';
import {
    useDrag,
    useDrop,
    DropTargetMonitor,
} from 'react-dnd';
import { XYCoord } from 'dnd-core';
import { getColumnHeaderLabel } from 'author/src/features/addQuestions/services/aiAssistedMarkingService';
import * as styles from './ColumnCard.scss';

interface ColumnCardProps {
    description: any;
    column: any;
    moveCard: (dragIndex: number, hoverIndex: number) => void;
    getIndexInFullByRef: (columnRef: string) => number;
    onRemove: (columnRef: string) => void;
}

const type = 'COLUMN';

interface DragItem {
    ref: string;
}

const DROP_POSITION = {
    BEFORE: 'before',
    AFTER: 'after',
} as const;

type DropDirection = typeof DROP_POSITION[keyof typeof DROP_POSITION];
type DropPosition = DropDirection | null;

function ColumnCard(props: ColumnCardProps) {
    const {
        description,
        onRemove,
        moveCard,
        column,
        getIndexInFullByRef,
    } = props;
    const elementRef = React.useRef<HTMLDivElement>(null);
    const cardRef = React.useRef<HTMLDivElement>(null);
    const [dropPosition, setDropPosition] = React.useState<DropPosition>(null);
    const dropPositionRef = React.useRef<DropPosition>(null);

    const updateDropPosition = React.useCallback((position: DropPosition) => {
        dropPositionRef.current = position;
        setDropPosition(position);
    }, []);

    const getTargetIndex = React.useCallback((dragIndex: number, hoverIndex: number, position: DropDirection): number => {
        if (position === DROP_POSITION.AFTER) {
            return dragIndex < hoverIndex ? hoverIndex : hoverIndex + 1;
        }

        return dragIndex < hoverIndex ? hoverIndex - 1 : hoverIndex;
    }, []);

    const [{ isDragging }, drag] = useDrag(
        () => ({
            type,
            item: () => ({ ref: column.ref }),
            end: () => {
                updateDropPosition(null);
            },
            collect: (monitor: any) => ({
                isDragging: monitor.isDragging(),
            }),
        }),
        [column.ref],
    );

    const [{ handlerId, isOver }, drop]: any = useDrop(() => ({
        hover(item: DragItem, monitor: DropTargetMonitor): any {
            if (!elementRef.current || !cardRef.current || !item?.ref || item.ref === column.ref) {
                updateDropPosition(null);
                return undefined;
            }

            const dragIndex = getIndexInFullByRef(item.ref);
            const hoverIndex = getIndexInFullByRef(column.ref);

            if (dragIndex < 0 || hoverIndex < 0 || dragIndex === hoverIndex) {
                updateDropPosition(null);
                return undefined;
            }

            // Use cardRef (not elementRef) so the spacer doesn't affect the midpoint calculation
            const hoverBoundingRect = cardRef.current.getBoundingClientRect();
            const hoverMiddleY = (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2;
            const clientOffset = monitor.getClientOffset();

            if (!clientOffset) {
                updateDropPosition(null);
                return undefined;
            }

            const hoverClientY = (clientOffset as XYCoord).y - hoverBoundingRect.top;
            const nextDropPosition = hoverClientY < hoverMiddleY ? DROP_POSITION.BEFORE : DROP_POSITION.AFTER;
            const targetIndex = getTargetIndex(dragIndex, hoverIndex, nextDropPosition);

            updateDropPosition(targetIndex === dragIndex ? null : nextDropPosition);

            return undefined;
        },
        drop(item: DragItem): any {
            const currentDropPosition = dropPositionRef.current;

            if (!item?.ref || !currentDropPosition || item.ref === column.ref) {
                return undefined;
            }

            const dragIndex = getIndexInFullByRef(item.ref);
            const hoverIndex = getIndexInFullByRef(column.ref);

            if (dragIndex < 0 || hoverIndex < 0 || dragIndex === hoverIndex) {
                return undefined;
            }

            const targetIndex = getTargetIndex(dragIndex, hoverIndex, currentDropPosition);

            if (targetIndex !== dragIndex) {
                moveCard(dragIndex, targetIndex);
            }

            return undefined;
        },
        canDrop(item: DragItem) {
            return !!item?.ref && item.ref !== column.ref;
        },
        accept: type,
        collect(monitor: any) {
            return {
                handlerId: monitor.getHandlerId(),
                isOver: monitor.isOver({ shallow: true }),
            };
        },
    }), [column.ref, getIndexInFullByRef, getTargetIndex, moveCard, updateDropPosition]);

    React.useEffect(() => {
        if (!isOver) {
            updateDropPosition(null);
        }
    }, [isOver, updateDropPosition]);

    const {
        name, fieldDefinitionId, ref,
    } = column;
    const opacity = isDragging ? 0 : 1;
    const handleClick = () => {
        onRemove(ref);
    };

    const label = fieldDefinitionId
        ? name
        : getColumnHeaderLabel(ref);

    drag(drop(elementRef));

    return (
        <div ref={elementRef} data-handler-id={handlerId}>
            {dropPosition === DROP_POSITION.BEFORE ? <div className={styles.dropSpacer} /> : null}
            <div ref={cardRef} className={styles.columnCard} style={{ opacity }}>
                <div className={styles.columnCardText}>
                    <Icon name="th" />
                    <span>{label}</span>
                    <span className={styles.grayText}>{description}</span>
                </div>
                <Icon id={ref} onClick={handleClick} name="delete" link />
            </div>
            {dropPosition === DROP_POSITION.AFTER ? <div className={styles.dropSpacer} /> : null}
        </div>
    );
}

export default ColumnCard;
